/** Normalize name autocomplete query for case-insensitive Cégjelző search. */
export const normalizeCegjelzoNameSearchQuery = (query) => {
  if (typeof query !== "string") return "";
  return query.trim().toLocaleLowerCase("hu-HU");
};

/** True when the query looks like a tax number (digits with optional HU separators). */
export const isTaxNumberLikeQuery = (query) => {
  const trimmed = query.trim();
  if (!trimmed) return false;
  if (!/\d/.test(trimmed)) return false;
  return /^[\d\s.\-/]+$/.test(trimmed);
};

/** Minimum digit count before calling Cégjelző tax search (adótörzs). */
export const CEGJELZO_TAX_SEARCH_MIN_DIGITS = 8;

/**
 * Page size for Cégjelző `/autocomplete` calls. Matches the upstream default (30) so
 * the UI can detect a "full page" without sending an explicit `limit`, but is also
 * forwarded as `limit` to keep client/server explicit.
 */
export const CEGJELZO_AUTOCOMPLETE_PAGE_SIZE = 30;

/** Sentinel value used in the autocomplete option list to render the "show more" row. */
export const CEGJELZO_SHOW_MORE_SENTINEL = "__cegjelzo_show_more__";

export const isShowMoreOption = (option) =>
  !!option && option.full_tax_number === CEGJELZO_SHOW_MORE_SENTINEL;

export const buildShowMoreOption = () => ({
  name: "",
  collection: "companies",
  id: -1,
  address: "",
  full_tax_number: CEGJELZO_SHOW_MORE_SENTINEL,
});

export const countTaxDigits = (query) => query.replace(/\D/g, "").length;

/** Show "enter 8 digits" hint in the autocomplete list while typing a tax number. */
export const shouldShowTaxMinDigitsHint = (query) =>
  isTaxNumberLikeQuery(query) &&
  countTaxDigits(query) < CEGJELZO_TAX_SEARCH_MIN_DIGITS;

/**
 * Value for `/search?type=tax_number`: 8-digit adótörzs or full `xxxxxxxx-x-xx` when 11 digits are present.
 */
export const normalizeTaxNumberForCegjelzoSearch = (query) => {
  const digits = query.replace(/\D/g, "");
  if (digits.length < CEGJELZO_TAX_SEARCH_MIN_DIGITS) return null;
  if (digits.length >= 11) {
    return `${digits.slice(0, 8)}-${digits.slice(8, 9)}-${digits.slice(9, 11)}`;
  }
  return digits.slice(0, 8);
};

const formatFullTaxNumber = (raw, fallbackId) => {
  if (raw === undefined || raw === null) {
    return fallbackId !== undefined ? String(fallbackId) : "";
  }
  const asString = String(raw);
  const digits = asString.replace(/\D/g, "");
  if (digits.length === 11) {
    return `${digits.slice(0, 8)}-${digits.slice(8, 9)}-${digits.slice(9, 11)}`;
  }
  if (/^\d{8}-\d-\d{2}$/.test(asString)) {
    return asString;
  }
  if (digits.length === 8) {
    return digits;
  }
  return asString;
};

const formatNavAddressLine = (nav) => {
  if (!nav) return "";
  const parts = [
    nav.zip,
    nav.city,
    nav.place_name,
    nav.place_category,
    nav.number,
    nav.building,
    nav.staircase,
    nav.floor,
    nav.door,
  ].filter((p) => p !== undefined && p !== null && String(p).trim() !== "");
  return parts.join(" ").trim();
};

/** Maps `/search` entities to autocomplete dropdown rows (same shape as `/autocomplete`). */
export const mapSearchEntityToAutocompleteResult = (entity) => {
  const e = entity;
  const typeStr = String(e.type ?? "").toLowerCase();

  if (typeStr === "group_tax_numbers" || typeStr === "4") {
    return null;
  }

  if (typeStr === "companies" || typeStr === "0") {
    const id = typeof e.id === "number" ? e.id : 0;
    const longNameArr = e.long_name;
    const shortNameArr = e.short_name;
    const name = longNameArr?.[0]?.name ?? shortNameArr?.[0]?.name ?? "";
    const addressArr = e.address;
    const address =
      addressArr?.[0]?.address ?? formatNavAddressLine(e.nav_address) ?? "";
    const taxArr = e.tax_number;
    const full_tax_number = formatFullTaxNumber(taxArr?.[0]?.tax_number, id);
    if (!name && !full_tax_number) return null;
    return {
      name: name || full_tax_number,
      collection: "companies",
      id,
      address,
      full_tax_number,
    };
  }

  if (typeStr === "self_employed" || typeStr === "1") {
    const id = typeof e.tax_number === "number" ? e.tax_number : 0;
    const name = typeof e.name === "string" ? e.name : "";
    const addressArr = e.address;
    const address =
      addressArr?.[0]?.address ?? formatNavAddressLine(e.nav_address) ?? "";
    const full_tax_number = formatFullTaxNumber(
      typeof e.tax_number === "number" || typeof e.tax_number === "string"
        ? e.tax_number
        : undefined,
      id,
    );
    if (!name && !full_tax_number) return null;
    return {
      name: name || full_tax_number,
      collection: "self_employed",
      id,
      address,
      full_tax_number,
    };
  }

  if (typeStr === "civil_orgs" || typeStr === "2") {
    const name =
      (typeof e.long_name === "string" ? e.long_name : undefined) ??
      (typeof e.short_name === "string" ? e.short_name : undefined) ??
      "";
    const address = typeof e.address === "string" ? e.address : "";
    const taxRaw = typeof e.tax_number === "string" ? e.tax_number : undefined;
    const digits = taxRaw?.replace(/\D/g, "") ?? "";
    const id = digits.length >= 8 ? Number(digits.slice(0, 8)) : 0;
    const full_tax_number = formatFullTaxNumber(taxRaw, id);
    if (!name && !full_tax_number) return null;
    return {
      name: name || full_tax_number,
      collection: "civil_orgs",
      id,
      address,
      full_tax_number,
    };
  }

  return null;
};

export const mapSearchResponseToAutocompleteResults = (response) => {
  if (!Array.isArray(response)) return [];
  return response
    .map((entity) => mapSearchEntityToAutocompleteResult(entity))
    .filter((row) => row !== null);
};
